import classnames from 'classnames'
import { css } from 'emotion'
import * as React from 'react'

import { LoanStep, TermUnit } from 'even-ts-static'

import ThemeConsumer from 'embed/components/ThemeConsumer'
import { AprType, TRANSUNION_COMPANY_UUID } from 'embed/constants'
import { companyUuid as configCompanyUuid } from 'embed/constants/config'

import {
  APR_FIXED,
  APR_LABEL,
  APR_POSTFIX,
  APR_VARIABLE,
  getAprDisplay,
  getMaxAmountDisplay,
  getMonthlyPaymentDisplay,
  LOAN_AMOUNT_PREFIX,
  OPEN_TERM_LENGTH,
} from 'embed/utils/offer-display-utils'

import { getContrastingColor, pluralize } from 'embed/utils'
import { LoanOffer } from 'embed/constants/models'

import { Flag } from './flag'

const compactStyles = require('embed/styles/resultscompact')
const styles = require('embed/styles/results')

interface Props {
  aprSuffix?: JSX.Element | string
  bestOfferId?: string
  disclaimer: React.ReactNode
  isResultsView?: boolean
  monthlyPaymentSuffix?: React.ReactNode
  offer: LoanOffer
  onLoanOfferCtaClick: (event: React.MouseEvent<Element>) => void
  partnerLogo: React.ReactNode
  selectedStep: LoanStep
  setEl: (el: HTMLLIElement) => void
  shouldDisplayTransunionDisclaimer: boolean
  tooltipText?: string
  trackingPixel: React.ReactNode
  viewportWidth?: number
}

const ListItemCompact: React.SFC<Props> = ({
  aprSuffix,
  bestOfferId,
  disclaimer,
  isResultsView,
  monthlyPaymentSuffix,
  onLoanOfferCtaClick,
  offer,
  offer: { productSubType },
  partnerLogo,
  selectedStep,
  setEl,
  shouldDisplayTransunionDisclaimer,
  tooltipText,
  trackingPixel,
  viewportWidth,
}) => {
  const isTransunionSupply: boolean = configCompanyUuid === TRANSUNION_COMPANY_UUID
  const showFlag = offer.secured || offer.preApproved || offer.preQualified
  const shouldShowOfferAmount = selectedStep === LoanStep.Results && offer.maxAmount

  const flag = (
    <div className={styles.offerFlagContainer}>
      <Flag
        bestOfferId={bestOfferId}
        isCompact={true}
        isResultsView={isResultsView}
        offer={offer}
        shouldDisplayTransunionDisclaimer={shouldDisplayTransunionDisclaimer}
        tooltipText={tooltipText}
        viewportWidth={viewportWidth}
      />
    </div>
  )

  const loanOfferTermLength =
    offer.termUnit === TermUnit.Open
      ? `${OPEN_TERM_LENGTH} term`
      : `${offer.termLength} ${pluralize(offer.termUnit, offer.termLength)}`

  return (
    <ThemeConsumer>
      {({ colorBorder, ctaStyles, colorTextPrimary, colorTextSecondary, fontFamilyPrimary }) => (
        <li className={compactStyles.offer} ref={setEl}>
          {trackingPixel}
          <div
            className={classnames(
              compactStyles.offerWrapper,
              compactStyles.loans,
              { [compactStyles.showFlag]: showFlag },
              css`
                border-color: ${colorBorder};
              `,
            )}
          >
            {flag}
            <div className={classnames(compactStyles.column, compactStyles.partnerLogo)}>
              <div>{partnerLogo}</div>
              {disclaimer}
            </div>
            <div className={classnames(compactStyles.column, compactStyles.monthlyPayment)}>
              <div className={compactStyles.bigStat}>{getMonthlyPaymentDisplay(offer)}</div>
              {/* Don't show the Monthly Payment term details if neither exist */}
              {(offer.meanMonthlyPayment || offer.monthlyPayment) && (
                <React.Fragment>
                  <div>
                    Per {offer.termUnit}
                    {monthlyPaymentSuffix}
                  </div>
                  <div>For {loanOfferTermLength}</div>
                </React.Fragment>
              )}
            </div>
            <div className={classnames(compactStyles.column, compactStyles.meanApr)}>
              <div className={compactStyles.bigStat}>
                {getAprDisplay(offer.meanApr)}
                {APR_POSTFIX}
              </div>
              <div>
                {offer.aprType && `${offer.aprType === AprType.Variable ? APR_VARIABLE : APR_FIXED} `}
                {APR_LABEL}
                {aprSuffix}
              </div>
            </div>
            <div className={classnames(compactStyles.column, compactStyles.buttonCta)}>
              {shouldShowOfferAmount && (
                <div className={classnames(compactStyles.bigStat, compactStyles.loanAmount)}>
                  {getMaxAmountDisplay(offer, LOAN_AMOUNT_PREFIX)}
                </div>
              )}
              <button
                className={classnames(
                  styles.button,
                  compactStyles.button,
                  css(`
                color: ${getContrastingColor(ctaStyles?.backgroundColor, { black: colorTextPrimary })};
                &:hover {opacity: 0.84;}
              `),
                )}
                style={ctaStyles}
                onClick={onLoanOfferCtaClick}
              >
                Continue
              </button>
              {isTransunionSupply && isResultsView && (
                <div
                  className={classnames(
                    styles.tuDisclaimer,
                    css`
                      color: ${colorTextSecondary};
                    `,
                  )}
                >
                  This is not a guaranteed approval
                </div>
              )}
            </div>
          </div>
        </li>
      )}
    </ThemeConsumer>
  )
}

export default ListItemCompact
